import React, { useEffect } from 'react';
import XCircleIcon from '../../../../ui/icons/XCircle/XCircle';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../../../hooks/Analytics';
import PanelContentHeader from '../../../../ui/PanelContentHeader/PanelContentHeader';
import { useTranslations } from '../../../../../hooks/useTranslations';
import { useTheme } from '../../../../../hooks/useTheme';
import { LayoutTree } from '../../../DynamicLayout';
import { Channels } from '../../types';
import styles from './ErrorAPI.module.css';

const { FeatureLayout } = LayoutTree;

export interface ErrorProps {
  featureName?: string;
  layout?: LayoutTree.FeatureLayout;
  channel: Channels;
  customApiErrorMessage?: string;
}

export interface ErrorAPIChannelProps {
  apiErrorHeader: string;
  apiErrorMessage: string;
  eventComponent: string;
}

const ErrorAPI = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
  channel,
  customApiErrorMessage,
}: ErrorProps) => {
  const { bodytextDefaultNone: errorMessageStyle } = useTheme();

  const {
    notificationCenterSmsApiErrorHeader,
    notificationCenterSmsApiErrorMessage,
    notificationCenterEmailApiErrorHeader,
    notificationCenterEmailApiErrorMessage,
    notificationCenterLineApiErrorHeader,
    notificationCenterLineApiErrorMessage,
  } = useTranslations();

  const logEvent = useAnalyticsPost();

  const isLandscape = layout === FeatureLayout.LANDSCAPE;

  const smsErrorChannelProps: ErrorAPIChannelProps = {
    apiErrorHeader: notificationCenterSmsApiErrorHeader || '',
    apiErrorMessage: notificationCenterSmsApiErrorMessage || '',
    eventComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
  };

  const emailErrorChannelProps: ErrorAPIChannelProps = {
    apiErrorHeader: notificationCenterEmailApiErrorHeader || '',
    apiErrorMessage: notificationCenterEmailApiErrorMessage || '',
    eventComponent: NarvarEventComponents.NTH_PACK_EMAIL_NOTIFICATION_CENTER,
  };

  const lineErrorChannelProps: ErrorAPIChannelProps = {
    apiErrorHeader: notificationCenterLineApiErrorHeader || '',
    apiErrorMessage: notificationCenterLineApiErrorMessage || '',
    eventComponent: NarvarEventComponents.NTH_PACK_LINE_NOTIFICATION_CENTER,
  };

  const errorChannelProps = {
    [Channels.SMS]: smsErrorChannelProps,
    [Channels.HIPAA_SMS]: smsErrorChannelProps,
    [Channels.EMAIL]: emailErrorChannelProps,
    [Channels.HIPAA_EMAIL]: emailErrorChannelProps,
    [Channels.LINE]: lineErrorChannelProps,
    [Channels.SUBLIME]: smsErrorChannelProps,
  };

  const {
    apiErrorHeader = '',
    apiErrorMessage = '',
    eventComponent = '',
  } = channel !== Channels.APP_PUSH ? errorChannelProps[channel] : {};

  useEffect(() => {
    logEvent({
      eventComponent,
      eventFeature: eventComponent,
      eventNotificationSignUpError: apiErrorMessage,
      eventType: NarvarEventTypes.ERROR,
      feature: featureName,
      pageComponent: eventComponent,
    });
  }, [apiErrorMessage, eventComponent, featureName, logEvent]);

  return (
    <div
      className={
        isLandscape ? styles.errorContainerLandscape : styles.errorContainer
      }
      data-test={`${channel}NotificationCenterErrorAPI`}
    >
      <PanelContentHeader
        as="p"
        icon={<XCircleIcon />}
        headerText={apiErrorHeader || ''}
        headerTextRole="status"
        paragraphChildren={
          <div style={errorMessageStyle}>
            {customApiErrorMessage ?? apiErrorMessage}
          </div>
        }
        isLandscape={isLandscape}
      />
    </div>
  );
};

export default ErrorAPI;
